import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import type { AxiosError } from 'axios';
import type React from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { CompanyInformationSection } from '../components/CompanyInfo/CompanyInformationSection';
import { InsuranceDetailsSection } from '../components/CompanyInfo/InsuranceDetailsSection';
import { PrimaryContactSection } from '../components/CompanyInfo/PrimaryContactSection';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import {
  type CompanyFormData,
  companyInfoSchema,
  initialCompanyFormData,
} from '../schemas/companyInfoSchema';
import { apiService } from '../services/api';
import type { ClientUpdate, TeamUpdate } from '../services/userService';
import { PERMISSIONS } from '../shared/constants/permissions';
import { formatDateForInput } from '../utils/dateUtils';

const CompanyInfoPage: React.FC = () => {
  const { idToken } = useAuth();
  const { setHasMissingAccountInfo, hasMissingAccountInfo, setIsFirstLogin, account } =
    useAccount();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const isRestricted = !account?.cachedPermissions?.includes(PERMISSIONS.CLIENT_PROFILE_UPDATE);
  const isClientSettingsDisabled = isRestricted || account?.role === 'team_lead';

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty: _isDirty },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: initialCompanyFormData,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [timezones, setTimezones] = useState<string[]>([]);

  useEffect(() => {
    try {
      const supportedTimezones = Intl.supportedValuesOf('timeZone');
      setTimezones(supportedTimezones);
    } catch (_e) {
      const fallbackTimezones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Australia/Sydney',
      ];
      setTimezones(fallbackTimezones);
    }
  }, []);

  const formatNumberWithCommas = useCallback((value: string | number): string => {
    if (value === '$' || value === '' || value === null || value === undefined) {
      return '';
    }
    const cleanValue = String(value).replace(/[$,]/g, '');
    if (cleanValue.trim() === '' || Number.isNaN(parseFloat(cleanValue))) {
      return String(value);
    }
    const num = parseFloat(cleanValue);
    return `$${num.toLocaleString('en-US')}`;
  }, []);

  const checkMissingInfo = useCallback(
    (currentData: CompanyFormData) => {
      let missing = false;
      if (!currentData.clientName || currentData.clientName.trim() === '') {
        missing = true;
      }
      if (
        !currentData.company_contact_email ||
        !companyInfoSchema.shape.company_contact_email.safeParse(currentData.company_contact_email)
          .success
      ) {
        missing = true;
      }

      if (typeof setHasMissingAccountInfo === 'function') {
        setHasMissingAccountInfo(missing);
      }
    },
    [setHasMissingAccountInfo]
  );

  useEffect(() => {
    const fetchDetails = async () => {
      if (!idToken) {
        setIsFetching(false);
        return;
      }
      setIsFetching(true);
      try {
        const details = await apiService.getAccountDetails();
        const { account: accountData } = details;

        // Merge fetched data with initial defaults to ensure no undefined values
        const newFormData: CompanyFormData = { ...initialCompanyFormData, ...accountData };

        // Ensure all fields are at least empty strings if undefined/null from backend
        for (const key of Object.keys(initialCompanyFormData) as (keyof CompanyFormData)[]) {
          if (newFormData[key] === undefined || newFormData[key] === null) {
            newFormData[key] = '';
          }
        }

        // Format date fields before resetting the form
        const dateFields: (keyof CompanyFormData)[] = [
          'liability_insurance_expiry',
          'indemnity_insurer_expiry',
          'workers_compensation_expiry_1',
          'workers_compensation_expiry_2',
          'workers_compensation_expiry_3',
          'workers_compensation_expiry_4',
        ];

        for (const field of dateFields) {
          if (newFormData[field]) {
            newFormData[field] = formatDateForInput(newFormData[field] as string);
          }
        }

        const numberFields = [
          'liability_insurance_per_claim',
          'liability_insurance_aggregate',
          'indemnity_insurer_per_claim',
          'indemnity_insurer_aggregate',
        ];

        for (const field of numberFields) {
          const key = field as keyof CompanyFormData;
          if (newFormData[key]) {
            newFormData[key] = newFormData[key]?.toString();
          }
        }

        reset(newFormData); // Reset the form with fetched data
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load account details.';
        showSnackbar(
          ((error as AxiosError)?.response?.data as { message: string })?.message || errorMessage,
          'error'
        );
      } finally {
        setIsFetching(false);
      }
    };
    fetchDetails();
  }, [idToken, showSnackbar, reset]);

  const onSave = async (data: CompanyFormData) => {
    if (!idToken) {
      showSnackbar('Authentication error. Please re-login.', 'error');
      return;
    }
    setIsLoading(true);

    const clientFields: (keyof ClientUpdate)[] = ['clientName'];
    const teamFields: (keyof TeamUpdate)[] = Object.keys(data).filter(
      (key) => !clientFields.includes(key as keyof ClientUpdate)
    ) as (keyof TeamUpdate)[];

    const clientDataToSave: ClientUpdate = {};
    if (account?.role !== 'team_lead') {
      for (const field of clientFields) {
        const value = (data as unknown as Record<string, unknown>)[field];
        if (value !== undefined) {
          (clientDataToSave as Record<string, unknown>)[field] = value;
        }
      }
    }

    const teamDataToSave: TeamUpdate = {};
    for (const field of teamFields) {
      const fieldValue = data[field];
      if (fieldValue !== undefined) {
        // Convert all values to strings for TeamUpdate (numbers will be handled below)
        (teamDataToSave as Record<string, string | number | null>)[field] =
          typeof fieldValue === 'number' ? String(fieldValue) : fieldValue;
      }
    }
    const numberFields: (keyof TeamUpdate)[] = [
      'liability_insurance_per_claim',
      'liability_insurance_aggregate',
      'indemnity_insurer_per_claim',
      'indemnity_insurer_aggregate',
    ];
    for (const field of numberFields) {
      const value = teamDataToSave[field];
      if (value && typeof value === 'string') {
        const parsedValue = parseFloat(value.replace(/[^\d.-]/g, ''));
        teamDataToSave[field] = Number.isNaN(parsedValue) ? null : String(parsedValue);
      }
    }
    try {
      await apiService.updateAccountDetails({ client: clientDataToSave, team: teamDataToSave });
      showSnackbar('Account details saved successfully!', 'success');
      setIsFirstLogin(false);
      checkMissingInfo(data);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save details.';
      showSnackbar(
        ((error as AxiosError)?.response?.data as { message: string })?.message || errorMessage,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onError = (_errors: FieldErrors<CompanyFormData>) => {
    showSnackbar('Please fix the validation errors before saving.', 'error');
  };

  if (isFetching) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageContainer title="Company Details">
      <Typography variant="h5" sx={{ fontSize: '1rem', mt: 1, mb: 3, color: 'text.primary' }}>
        The details below will be used to autofill common sections of the tenders you pursue.
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(onSave, onError)}
        sx={{ maxWidth: 'lg', mx: 'auto', pb: 4, mt: 0 }}
      >
        <Paper
          elevation={3}
          sx={{ padding: theme.spacing(3), mb: 4, background: theme.palette.greyLight }}
        >
          {isRestricted && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                textAlign: 'center',
                fontWeight: 'bold',
                backgroundColor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                padding: theme.spacing(1.5, 2),
                borderRadius: theme.shape.borderRadius,
              }}
            >
              Only client administrators can edit company details.
            </Typography>
          )}
          {hasMissingAccountInfo && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                textAlign: 'center',
                fontWeight: 'bold',
                backgroundColor: theme.palette.bidlogiq?.rust,
                color: theme.palette.bidlogiq?.white,
                padding: theme.spacing(1.5, 2),
                borderRadius: theme.shape.borderRadius,
              }}
            >
              For the best results, please fill in all essential company and contact information.
            </Typography>
          )}

          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            {!isRestricted && (
              <Button
                variant="contained"
                name="Save Details Top"
                type="submit"
                disabled={isLoading || isFetching}
                sx={{
                  fontSize: '1rem',
                  color: theme.palette.bidlogiq?.white,
                  backgroundColor: theme.palette.bidlogiq?.brick,
                  '&:hover': {
                    backgroundColor: theme.palette.bidlogiq?.rust,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.bidlogiq?.white }} />
                ) : (
                  'Save Details'
                )}
              </Button>
            )}
          </Box>

          <Grid container direction="column" spacing={3}>
            <CompanyInformationSection
              control={control}
              errors={errors}
              isRestricted={isRestricted}
              isClientSettingsDisabled={isClientSettingsDisabled}
              timezones={timezones}
            />
            <PrimaryContactSection control={control} errors={errors} isRestricted={isRestricted} />
            <InsuranceDetailsSection
              control={control}
              errors={errors}
              isRestricted={isRestricted}
              formatNumberWithCommas={formatNumberWithCommas}
            />
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            {!isRestricted && (
              <Button
                name="Save Details Bottom"
                variant="contained"
                type="submit"
                disabled={isLoading || isFetching}
                sx={{
                  fontSize: '1rem',
                  color: theme.palette.bidlogiq?.white,
                  backgroundColor: theme.palette.bidlogiq?.brick,
                  '&:hover': {
                    backgroundColor: theme.palette.bidlogiq?.rust,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.bidlogiq?.white }} />
                ) : (
                  'Save Details'
                )}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </PageContainer>
  );
};

export default memo(CompanyInfoPage);
